import type {
  IntervalResult,
  PreparedTrackingFrames,
  TrackingActivity,
  TrackingInterval,
  TrackingOptions,
  TrackingStage,
} from "../contracts";
import { DecodeClient } from "./decode-client";
import { TrackingClient } from "./tracking-client";
import {
  configurePixelBudget,
  pixelBudgetBytes,
  releasePixels,
  reservePixels,
  reservedPixelBytes,
} from "./pixel-budget";
import { TrackingScheduler, deviceComputeSlots, trackingAdmissionCapacity } from "./scheduler";
import { recoveryAbortError } from "../recovery/coordinator";
import type { RecoveryImage } from "../recovery/contracts";
import { SessionRecoveryQueue, type RefinementRequest } from "./session-recovery-queue";

export interface SessionConfig {
  src?: string;
  enabled: boolean;
  options: TrackingOptions;
  /**
   * Bumped for an explicit rerun. Part of the result cache's scope, so
   * "Rerun" genuinely recomputes instead of recalling what it is meant to
   * discard.
   */
  runKey?: number;
}

export interface SessionDependencies {
  createDecoder: (
    src: string,
    options: TrackingOptions,
  ) => Pick<DecodeClient, "decode" | "dispose" | "isDisposed"> & Partial<Pick<DecodeClient, "extractRecoveryImages">>;
  createTracker: (src: string, options: TrackingOptions) => Pick<TrackingClient, "process" | "dispose" | "isDisposed">;
  supportsWorkers: () => boolean;
  workerCount: () => number;
}

const browserDependencies: SessionDependencies = {
  createDecoder: (src, options) => new DecodeClient(src, options),
  createTracker: (src, options) => new TrackingClient(src, options),
  supportsWorkers: () => typeof Worker !== "undefined",
  workerCount: deviceComputeSlots,
};

interface Slot {
  id: number;
  client: ReturnType<SessionDependencies["createTracker"]> | null;
  job?: Job;
}
interface Job {
  interval: TrackingInterval;
  slot: Slot;
  revision: number;
  recovery?: RefinementRequest;
}
interface Runtime {
  decoder: ReturnType<SessionDependencies["createDecoder"]>;
  slots: Slot[];
  scheduler: TrackingScheduler;
  decoding: boolean;
}
interface Execution {
  runtime?: Runtime;
}

interface RuntimeCallbacks {
  activity: (patch: Partial<Pick<TrackingActivity, "active" | "workerCount">>) => void;
  progress: (intervalId: string, workerId: number, stage: TrackingStage) => void;
  result: (result: IntervalResult, complete: boolean) => void;
  finish: (intervalId: string) => void;
  error: (intervalId: string, error: unknown) => void;
  unavailable: (error: unknown) => void;
}

/** Execution only: one admission loop owns decoder access, worker slots and pixel reservations. */
export class SessionRuntime {
  private readonly dependencies: SessionDependencies;
  private intervals: readonly TrackingInterval[] = [];
  private intervalIds = new Set<string>();
  private playhead = 0;
  private attempted = new Set<string>();
  private revisions = new Map<string, number>();
  private execution: Execution | null = null;
  private unavailableReason?: string;
  private readonly recoveryQueue = new SessionRecoveryQueue(() => this.pumpCurrent());
  private preferRecovery = false;

  constructor(
    private readonly config: SessionConfig,
    dependencies: Partial<SessionDependencies>,
    private readonly callbacks: RuntimeCallbacks,
  ) {
    this.dependencies = { ...browserDependencies, ...dependencies };
  }

  /** Recovery reconciliation can immediately enqueue work at the new playhead. */
  updatePlayhead(playhead: number): void {
    this.playhead = playhead;
  }

  reconcile(intervals: readonly TrackingInterval[]): void {
    if (this.intervals === intervals) return;
    this.intervals = intervals;
    this.intervalIds = new Set(intervals.map((interval) => interval.id));
    for (const id of this.attempted) {
      if (this.intervalIds.has(id)) continue;
      this.attempted.delete(id);
      this.revisions.set(id, (this.revisions.get(id) ?? 0) + 1);
    }
  }

  /**
   * Treat these intervals as already processed, so the scheduler neither
   * queues them nor counts them as outstanding. Used to adopt results recalled
   * from the cross-session cache.
   */
  markAttempted(ids: Iterable<string>): void {
    for (const id of ids) if (this.intervalIds.has(id)) this.attempted.add(id);
  }

  /**
   * `onReady` runs after this runtime's state is reset and before the first
   * admission pass, which is the only point where adopting cached work counts:
   * `ensure()` pumps immediately, so anything marked after it would already
   * have been dispatched.
   */
  start(onReady?: () => void): () => void {
    const execution: Execution = {};
    this.execution = execution;
    this.attempted.clear();
    this.revisions.clear();
    this.unavailableReason = undefined;
    onReady?.();
    this.ensure();
    return () => {
      if (this.execution !== execution) return;
      this.stopWorkers();
      this.execution = null;
    };
  }

  pumpCurrent(): void {
    const runtime = this.execution?.runtime;
    if (runtime) this.pump(runtime);
  }

  private unavailable(error: unknown): void {
    this.unavailableReason = error instanceof Error ? error.message : String(error);
    this.recoveryQueue.rejectAll(error);
    this.callbacks.unavailable(error);
  }

  ensure(): void {
    const execution = this.execution;
    if (!execution || !this.config.enabled || this.unavailableReason) return;
    if (!this.config.src || !this.dependencies.supportsWorkers()) {
      this.unavailable(
        !this.config.src ? "Video source is not available." : "This browser does not support Web Workers.",
      );
      return;
    }
    if (!this.intervals.length) return;
    if (execution.runtime) {
      this.pump(execution.runtime);
      return;
    }
    const capacity = this.dependencies.workerCount();
    configurePixelBudget(capacity);
    const slots: Slot[] = [];
    let decoder: Runtime["decoder"] | undefined;
    try {
      decoder = this.dependencies.createDecoder(this.config.src, this.config.options);
      for (let id = 0; id < capacity; id++) {
        try {
          slots.push({ id, client: this.dependencies.createTracker(this.config.src, this.config.options) });
        } catch (error) {
          if (slots.length === 0) throw error;
          break; // The optional second worker must not prevent the first from completing the queue.
        }
      }
    } catch (error) {
      slots.forEach((slot) => slot.client?.dispose());
      decoder?.dispose();
      this.unavailable(error);
      return;
    }
    const runtime: Runtime = {
      decoder,
      slots,
      scheduler: new TrackingScheduler(),
      decoding: false,
    };
    execution.runtime = runtime;
    this.callbacks.activity({ workerCount: slots.length });
    this.pump(runtime);
  }

  stopWorkers(): void {
    this.unavailableReason = undefined;
    const runtime = this.execution?.runtime;
    if (!runtime) return;
    this.execution!.runtime = undefined;
    // Invalidate before disposal rejects outstanding promises.
    for (const slot of runtime.slots) if (slot.job) this.finish(runtime, slot.job);
    runtime.decoder.dispose();
    runtime.slots.forEach((slot) => slot.client?.dispose());
    this.callbacks.activity({ active: new Map(), workerCount: 0 });
  }

  private isCurrent(runtime: Runtime, job: Job): boolean {
    const id = job.recovery?.parent.id ?? job.interval.id;
    return (
      this.execution?.runtime === runtime &&
      job.slot.job === job &&
      job.revision === (this.revisions.get(id) ?? 0) &&
      this.intervalIds.has(id) &&
      !job.recovery?.signal.aborted
    );
  }

  private progress(runtime: Runtime, job: Job, stage: TrackingStage): void {
    if (job.recovery || !this.isCurrent(runtime, job)) return;
    this.callbacks.progress(job.interval.id, job.slot.id, stage);
  }

  private save(runtime: Runtime, job: Job, result: IntervalResult, complete: boolean): void {
    if (!this.isCurrent(runtime, job) || result.interval.id !== job.interval.id) return;
    if (job.recovery) {
      if (complete) job.recovery.resolve(result);
      return;
    }
    this.callbacks.result(result, complete);
  }

  private finish(runtime: Runtime, job: Job, error?: unknown): void {
    if (job.recovery && error !== undefined) job.recovery.reject(error);
    else if (this.isCurrent(runtime, job) && error !== undefined) this.callbacks.error(job.interval.id, error);
    if (job.slot.job !== job) return;
    job.slot.job = undefined;
    releasePixels(this.config.options.maxIntervalBytes);
    if (this.execution?.runtime !== runtime) return;
    if (job.recovery) return;
    this.callbacks.finish(job.interval.id);
  }

  private replaceFailedTracker(runtime: Runtime, slot: Slot): void {
    if (this.execution?.runtime !== runtime || !slot.client?.isDisposed) return;
    try {
      slot.client = this.dependencies.createTracker(this.config.src!, this.config.options);
    } catch (error) {
      slot.client = null;
      const workerCount = runtime.slots.filter((candidate) => candidate.client !== null).length;
      this.callbacks.activity({ workerCount });
      if (!workerCount) this.unavailable(error);
    }
  }

  private async compute(runtime: Runtime, job: Job, prepared: PreparedTrackingFrames): Promise<void> {
    try {
      if (this.isCurrent(runtime, job)) {
        const result = await job.slot.client!.process(
          job.interval,
          (preview) => this.save(runtime, job, preview, false),
          (stage) => this.progress(runtime, job, stage),
          prepared,
        );
        this.save(runtime, job, result, true);
      }
    } catch (error) {
      if (job.recovery) job.recovery.reject(error);
      else if (this.isCurrent(runtime, job)) this.callbacks.error(job.interval.id, error);
      this.replaceFailedTracker(runtime, job.slot);
    } finally {
      this.finish(runtime, job);
      this.pump(runtime);
    }
  }

  extractRecovery(base: IntervalResult, targetTime: number, signal: AbortSignal): Promise<RecoveryImage[]> {
    if (signal.aborted || !this.execution?.runtime) return Promise.reject(recoveryAbortError());
    if (this.unavailableReason) return Promise.reject(new Error(this.unavailableReason));
    return this.recoveryQueue.extract(base, targetTime, signal);
  }

  refineRecovery(
    parent: TrackingInterval,
    children: readonly TrackingInterval[],
    signal: AbortSignal,
  ): Promise<IntervalResult[]> {
    if (!children.length) return Promise.resolve([]);
    if (signal.aborted || !this.execution?.runtime) return Promise.reject(recoveryAbortError());
    if (this.unavailableReason) return Promise.reject(new Error(this.unavailableReason));
    return this.recoveryQueue.refine(parent, children, signal);
  }

  private pumpExtraction(runtime: Runtime): boolean {
    const extraction = this.recoveryQueue.peekExtraction(this.playhead);
    if (!extraction) return false;
    // Account for the source sample plus target canvas/JPEG encoding alongside local ROI pixels.
    const reservation = extraction.base.decode.sourceWidth * extraction.base.decode.sourceHeight * 4 + 32 * 1024 * 1024;
    if (reservation > pixelBudgetBytes()) {
      this.recoveryQueue.takeExtraction();
      extraction.reject(new Error("Recovery frame extraction exceeds the player's pixel memory budget"));
      queueMicrotask(() => this.pump(runtime));
      return true;
    }
    if (!reservePixels(reservation)) return false;
    this.recoveryQueue.takeExtraction();
    runtime.decoding = true;
    this.preferRecovery = false;
    const decoder = runtime.decoder;
    const abort = () => decoder.dispose();
    extraction.signal.addEventListener("abort", abort, { once: true });
    const operation = decoder.extractRecoveryImages;
    const promise = operation
      ? operation.call(decoder, [
          { timestamp: extraction.targetTime, maxSize: 1536, exact: true },
          { timestamp: extraction.base.interval.start.time, maxSize: 768 },
          { timestamp: extraction.base.interval.end.time, maxSize: 768 },
        ])
      : Promise.reject(new Error("Recovery frame extraction is not available"));
    void promise.then(extraction.resolve, extraction.reject).finally(() => {
      extraction.signal.removeEventListener("abort", abort);
      runtime.decoding = false;
      releasePixels(reservation);
      if (this.execution?.runtime === runtime && runtime.decoder.isDisposed) {
        try {
          runtime.decoder = this.dependencies.createDecoder(this.config.src!, this.config.options);
        } catch (error) {
          this.unavailable(error);
        }
      }
      this.pump(runtime);
    });
    return true;
  }

  private pump(runtime: Runtime): void {
    if (this.execution?.runtime !== runtime || runtime.decoding || this.unavailableReason) return;
    this.recoveryQueue.discardCancelled();
    const idle = runtime.slots.filter((slot) => slot.client && !slot.job);
    const reservation = this.config.options.maxIntervalBytes;
    const available = trackingAdmissionCapacity(idle.length, reservedPixelBytes(), reservation, pixelBudgetBytes());
    const localsDone = this.attempted.size >= this.intervals.length;
    if ((this.preferRecovery || localsDone || !available) && this.pumpExtraction(runtime)) return;
    if (!available) return;
    const inFlight = new Set(runtime.slots.flatMap((slot) => (slot.job ? [slot.job.interval.id] : [])));
    const waiting = this.intervals.filter((interval) => !inFlight.has(interval.id));
    const refinement = this.recoveryQueue.takeRefinement(this.playhead, this.preferRecovery || localsDone);
    const batch = refinement
      ? [refinement.interval]
      : runtime.scheduler.takeBatch(waiting, this.attempted, this.playhead, available);
    if (!batch.length && this.pumpExtraction(runtime)) return;
    if (!batch.length) return;
    this.preferRecovery = !refinement;
    const jobs = batch.map((interval, index) => {
      const slot = idle[index]!;
      const job: Job = {
        interval,
        slot,
        revision: this.revisions.get(refinement?.parent.id ?? interval.id) ?? 0,
        recovery: refinement,
      };
      slot.job = job;
      reservePixels(reservation);
      this.progress(runtime, job, "decoding");
      return job;
    });
    runtime.decoding = true;
    void runtime.decoder
      .decode(batch)
      .then((decoded) => {
        runtime.decoding = false;
        for (const job of jobs) {
          const output = decoded.find((item) => item.interval.id === job.interval.id);
          if (!output || output.error !== undefined)
            this.finish(runtime, job, output?.error ?? "Decoder returned no interval output");
          else void this.compute(runtime, job, { decoded: output.decoded, decodeMs: output.decodeMs });
        }
        this.pump(runtime);
      })
      .catch((error: unknown) => {
        runtime.decoding = false;
        jobs.forEach((job) => this.finish(runtime, job, error));
        if (this.execution?.runtime !== runtime) return;
        if (runtime.decoder.isDisposed) {
          try {
            runtime.decoder = this.dependencies.createDecoder(this.config.src!, this.config.options);
          } catch (replacementError) {
            this.unavailable(replacementError);
          }
        }
        this.pump(runtime);
      });
  }
}
